/**
 * wire-app entry — the front page. Boots the masthead, the three
 * teasers, the sealed premium card, and the editions shelf, then asks
 * the sketch engine to dress everything it can find.
 */

import { EDITION } from "./data";
import { renderEditions } from "./editions";
import { renderPremiumCard, renderStories } from "./articles";
import { renderMasthead } from "./masthead";
import { colophonLine } from "./reader-profile";
import { almanac, renderable as weatherRenderable } from "./weather";

type SketchApi = {
  wireDivider(el: HTMLElement): unknown;
  wireButton(el: HTMLElement, opts?: { variant?: "outline" | "solid" | "scribble" }): unknown;
  wireCard(el: HTMLElement): unknown;
  wireBadge(el: HTMLElement, opts?: { variant?: "outline" | "scribble" }): unknown;
  packs: {
    roughGlobe(cx: number, cy: number, r: number, opts?: { seed?: number }): string[];
    roughDuck(x: number, y: number, s: number, opts?: { seed?: number }): string[];
    roughCapsule(x: number, y: number, w: number, h: number, opts?: { seed?: number }): string[];
    roughSeal(cx: number, cy: number, r: number, opts?: { seed?: number }): string[];
  };
  core: { mulberry32(seed: number): () => number };
};

function sketch(): SketchApi | null {
  return (window as unknown as { WireSketch?: SketchApi }).WireSketch ?? null;
}

/** Draw one of the art glyphs into a story's art box. */
function drawArt(box: HTMLElement, kind: string): void {
  const ws = sketch();
  if (!ws) return;
  const seedBase = Math.floor(ws.core.mulberry32(hashStr(kind))() * 1e9);
  const w = box.offsetWidth || 220;
  const h = box.offsetHeight || 140;
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
  svg.setAttribute("class", "ws-svg ws-boil");
  svg.setAttribute("aria-hidden", "true");
  let paths: string[] = [];
  if (kind === "globe") paths = ws.packs.roughGlobe(w / 2, h / 2, Math.min(w, h) * 0.34, { seed: seedBase });
  else if (kind === "duck") paths = ws.packs.roughDuck(w * 0.28, h * 0.22, Math.min(w, h) * 0.62, { seed: seedBase });
  else if (kind === "capsule") paths = ws.packs.roughCapsule(w * 0.3, h * 0.28, w * 0.4, h * 0.44, { seed: seedBase });
  else paths = ws.packs.roughCapsule(w * 0.34, h * 0.3, w * 0.32, h * 0.4, { seed: seedBase });
  for (const d of paths) {
    const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
    p.setAttribute("d", d);
    p.setAttribute("fill", "none");
    p.setAttribute("stroke", "var(--ws-ink)");
    p.setAttribute("stroke-width", "1.7");
    p.setAttribute("stroke-linecap", "round");
    svg.appendChild(p);
  }
  box.appendChild(svg);
}

function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function dressChrome(): void {
  const ws = sketch();
  if (!ws) return;
  for (const el of Array.from(document.querySelectorAll<HTMLElement>("[data-ws-divider]"))) ws.wireDivider(el);
  for (const el of Array.from(document.querySelectorAll<HTMLElement>(".wire-nav__item"))) {
    ws.wireButton(el, { variant: el.classList.contains("wire-nav__item--cta") ? "solid" : "outline" });
  }
  for (const el of Array.from(document.querySelectorAll<HTMLElement>(".wire-story__read"))) ws.wireButton(el);
  for (const el of Array.from(document.querySelectorAll<HTMLElement>(".wire-edition__lock"))) {
    ws.wireButton(el, { variant: "scribble" });
  }
  for (const el of Array.from(document.querySelectorAll<HTMLElement>(".wire-premium-card__cta"))) {
    ws.wireButton(el, { variant: "outline" });
  }
  for (const el of Array.from(document.querySelectorAll<HTMLElement>(".wire-kicker--premium"))) ws.wireBadge(el, { variant: "scribble" });
  const stamp = document.querySelector<HTMLElement>("[data-ws-seal]");
  if (stamp) {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    const size = 84;
    svg.setAttribute("viewBox", `0 0 ${size} ${size}`);
    svg.setAttribute("class", "ws-svg ws-boil");
    svg.setAttribute("aria-hidden", "true");
    const paths = ws.packs.roughSeal(size / 2, size / 2, size * 0.36, { seed: 41288 });
    for (const d of paths) {
      const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
      p.setAttribute("d", d);
      p.setAttribute("fill", "none");
      p.setAttribute("stroke", "var(--ws-accent, var(--ws-ink))");
      p.setAttribute("stroke-width", "1.6");
      p.setAttribute("stroke-linecap", "round");
      svg.appendChild(p);
    }
    stamp.appendChild(svg);
  }
  for (const box of Array.from(document.querySelectorAll<HTMLElement>("[data-ws-art]"))) {
    drawArt(box, box.dataset.wsArt as string);
  }
}

function boot(): void {
  const masthead = document.getElementById("wire-masthead");
  if (masthead) renderMasthead(masthead);
  const stories = document.getElementById("wire-stories");
  if (stories) renderStories(stories);
  // comment threads render after the stories exist (the comments chunk
  // boots first, before the story mounts are on the page)
  for (const mount of Array.from(document.querySelectorAll<HTMLElement>("[data-comments-for]"))) {
    (window as unknown as { __wire?: { comments?: { renderThread(m: HTMLElement, id: string): void } } })
      .__wire?.comments?.renderThread(mount, mount.dataset.commentsFor as string);
  }
  const premium = document.getElementById("wire-premium");
  if (premium) renderPremiumCard(premium);
  const editions = document.getElementById("wire-editions");
  if (editions) renderEditions(editions);

  const foot = document.getElementById("wire-footer");
  if (foot) {
    foot.innerHTML = `
      <div class="wire-footer__rule" data-ws-divider></div>
      <p>The Ordinary Wire — ordinary engineering's news desk. This page is static. The page is proud of this.</p>
      <p class="wire-footer__fine">${colophonLine()} · ${EDITION.build}</p>
      ${
        weatherRenderable()
          ? `<p class="wire-footer__fine">${almanac(document.documentElement.dataset.universe ?? "A-1")}</p>`
          : ""
      }`;
  }

  dressChrome();

  for (const item of Array.from(document.querySelectorAll<HTMLElement>("[data-nav]"))) {
    item.addEventListener("click", () => {
      const nav = item.dataset.nav;
      if (nav === "signup") {
        // the signup button always offers the signup; the wall can judge later
        (window as unknown as { __wire?: { paywall?: { gate: { presentSignup(): void } } } }).__wire?.paywall?.gate.presentSignup();
        return;
      }
      const target = nav === "editions" ? document.getElementById("wire-editions") : document.getElementById("wire-front");
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }
}

if (typeof window !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
}
